import { encodeAbiParameters, parseAbiParameters } from 'viem';

const TOKEN_SOURCE_CODE = `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.19;

contract CustomToken {
    string public name;
    string public symbol;
    uint8 public decimals;
    uint256 public totalSupply;
    mapping(address => uint256) public balanceOf;
    mapping(address => mapping(address => uint256)) public allowance;
    address public owner;
    uint256 public buyTax;
    uint256 public sellTax;
    uint256 public maxTxAmount;
    uint256 public maxWalletAmount;
    uint256 public tradingDelay;
    bool public tradingEnabled;
    uint256 public tradingEnabledAt;
    mapping(address => bool) public isExcludedFromFees;
    mapping(address => bool) public isExcludedFromLimits;

    event Transfer(address indexed from, address indexed to, uint256 value);
    event Approval(address indexed owner, address indexed spender, uint256 value);
    event OwnershipTransferred(address indexed previousOwner, address indexed newOwner);

    constructor(
        string memory _name,
        string memory _symbol,
        uint256 _totalSupply,
        uint8 _decimals,
        address _owner,
        uint256 _buyTax,
        uint256 _sellTax,
        uint256 _maxTxAmount,
        uint256 _maxWalletAmount,
        uint256 _tradingDelay
    ) {
        name = _name;
        symbol = _symbol;
        decimals = _decimals;
        totalSupply = _totalSupply;
        owner = _owner;
        buyTax = _buyTax;
        sellTax = _sellTax;
        maxTxAmount = _maxTxAmount;
        maxWalletAmount = _maxWalletAmount;
        tradingDelay = _tradingDelay;
        balanceOf[_owner] = _totalSupply;
        isExcludedFromFees[_owner] = true;
        isExcludedFromLimits[_owner] = true;
        emit Transfer(address(0), _owner, _totalSupply);
    }

    modifier onlyOwner() {
        require(msg.sender == owner, "Only owner can call this function");
        _;
    }

    function transfer(address to, uint256 amount) public returns (bool) {
        _transfer(msg.sender, to, amount);
        return true;
    }

    function approve(address spender, uint256 amount) public returns (bool) {
        allowance[msg.sender][spender] = amount;
        emit Approval(msg.sender, spender, amount);
        return true;
    }

    function transferFrom(address from, address to, uint256 amount) public returns (bool) {
        require(allowance[from][msg.sender] >= amount, "Insufficient allowance");
        allowance[from][msg.sender] -= amount;
        _transfer(from, to, amount);
        return true;
    }

    function _transfer(address from, address to, uint256 amount) internal {
        require(from != address(0), "Transfer from zero address");
        require(to != address(0), "Transfer to zero address");
        require(balanceOf[from] >= amount, "Insufficient balance");

        if (!isExcludedFromLimits[from] && !isExcludedFromLimits[to]) {
            if (maxTxAmount > 0) {
                require(amount <= maxTxAmount, "Transfer amount exceeds maxTxAmount");
            }
            if (maxWalletAmount > 0) {
                require(balanceOf[to] + amount <= maxWalletAmount, "Wallet amount exceeds maxWalletAmount");
            }
        }

        uint256 taxAmount = 0;
        if (!isExcludedFromFees[from] && !isExcludedFromFees[to]) {
            if (tradingEnabled) {
                require(block.timestamp >= tradingEnabledAt + tradingDelay, "Trading not yet enabled");
                taxAmount = (amount * (from == owner ? sellTax : buyTax)) / 10000;
            }
        }

        balanceOf[from] -= amount;
        balanceOf[to] += amount - taxAmount;
        if (taxAmount > 0) {
            balanceOf[owner] += taxAmount;
            emit Transfer(from, owner, taxAmount);
        }
        emit Transfer(from, to, amount - taxAmount);
    }

    function enableTrading() public onlyOwner {
        require(!tradingEnabled, "Trading already enabled");
        tradingEnabled = true;
        tradingEnabledAt = block.timestamp;
    }

    function setTaxes(uint256 _buyTax, uint256 _sellTax) public onlyOwner {
        require(_buyTax <= 2500 && _sellTax <= 2500, "Tax cannot exceed 25%");
        buyTax = _buyTax;
        sellTax = _sellTax;
    }

    function setMaxTxAmount(uint256 _maxTxAmount) public onlyOwner {
        maxTxAmount = _maxTxAmount;
    }

    function setMaxWalletAmount(uint256 _maxWalletAmount) public onlyOwner {
        maxWalletAmount = _maxWalletAmount;
    }

    function excludeFromFees(address account, bool excluded) public onlyOwner {
        isExcludedFromFees[account] = excluded;
    }

    function excludeFromLimits(address account, bool excluded) public onlyOwner {
        isExcludedFromLimits[account] = excluded;
    }

    function transferOwnership(address newOwner) public onlyOwner {
        require(newOwner != address(0), "New owner is the zero address");
        emit OwnershipTransferred(owner, newOwner);
        owner = newOwner;
    }
}`;

export async function verifyContract(
  tokenAddress: string,
  name: string,
  symbol: string,
  totalSupply: bigint,
  buyTax: number,
  sellTax: number,
  maxWalletPercent: number,
  maxBuyPercent: number,
  maxSellPercent: number,
  tradingDelay: number,
  pancakeRouter: string,
  owner: string
): Promise<boolean> {
  try {
    console.log("Starting contract verification for:", tokenAddress);
    console.log("Constructor arguments:", {
      name,
      symbol,
      totalSupply: totalSupply.toString(),
      buyTax,
      sellTax,
      maxWalletPercent,
      maxBuyPercent,
      maxSellPercent,
      tradingDelay,
      pancakeRouter,
      owner
    });

    // These need to match EXACTLY with the constructor parameters
    const constructorArguments = [
      name,
      symbol,
      totalSupply.toString(),
      buyTax,
      sellTax,
      maxWalletPercent,
      maxBuyPercent,
      maxSellPercent,
      tradingDelay,
      pancakeRouter,
      owner
    ];

    const hre = await import("hardhat");
    await hre.run("verify:verify", {
      address: tokenAddress,
      constructorArguments,
      contract: "contracts/V8TokenTemplate.sol:V8Token"
    });

    return true;
  } catch (error: any) {
    if (error?.message?.includes("Already Verified")) {
      console.log("Contract is already verified");
      return true;
    }
    console.error("Verification error:", error);
    return false;
  }
}
